import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FormInput } from '@/common/forms/FormInput';
import { mockUserData } from '~/mockData';

export const Route = createFileRoute('/_app/contact')({
  component: ContactPage,
  beforeLoad: () => {
    return {
      pageBackground: 'white',
      noContainer: true,
    };
  },
});

function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: `${mockUserData.firstName} ${mockUserData.lastName}`,
    email: mockUserData.email,
    message: '',
  });
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!recaptchaValue) {
      // Translation Needed
      alert('Please complete the reCAPTCHA verification');
      return;
    }

    // Handle form submission
    console.log('Form submitted with reCAPTCHA token:', recaptchaValue);
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  const isFormValid =
    formData.name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.message.trim() !== '' &&
    recaptchaValue !== null;

  return (
    <PageWrapper>
      <ScrollableContent>
        <Container>
          <Title>{t('wbs.contact-us')}</Title>
          <Form onSubmit={handleSubmit}>
            <FormInput
              label={t('wbs.name')}
              type="text"
              value={formData.name}
              onChange={(value) => handleInputChange('name', value)}
              alternate
            />
            <FormInput
              label={t('notifications.email')}
              type="email"
              value={formData.email}
              onChange={(value) => handleInputChange('email', value)}
              alternate
            />
            <FormGroup>
              <Label>{t('wbs.message')}</Label>
              <TextArea
                placeholder={t('wbs.message')}
                rows={6}
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
              />
            </FormGroup>
            <RecaptchaWrapper>
              <ReCAPTCHA
                // TODO: Add site key
                sitekey="YOUR_RECAPTCHA_SITE_KEY"
                onChange={handleRecaptchaChange}
              />
            </RecaptchaWrapper>
            <SubmitButton type="submit" disabled={!isFormValid}>
              {t('actions.submit')}
            </SubmitButton>
          </Form>
        </Container>
      </ScrollableContent>
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  background: url('https://picsum.photos/1920/1080') center center / cover
    no-repeat;
  background-attachment: fixed;
  min-height: 70vh;
  position: relative;
  width: 100%;
`;

const ScrollableContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 70vh;
  padding: 0px 20px;
  position: relative;
  width: 100%;
`;

const Container = styled.div`
  background: var(--white);
  margin: 0 auto;
  max-width: 600px;
  padding: 45px 100px 104px;
  width: 100%;
`;

const Title = styled.h1`
  color: var(--grey-900);
  font-family: var(--open-sans);
  font-family: var(--raleway);
  font-size: 41px;
  font-weight: 700;
  margin-bottom: 69px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  max-width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: var(--black);
  display: block;
  font-family: var(--raleway);
  font-size: 16px;
  font-weight: 300;
  letter-spacing: -0.25px;
  margin-bottom: 12px;
`;

const TextArea = styled.textarea`
  background-color: var(--grey-150);
  border: none;
  border-radius: 4px;
  box-shadow: var(--shadow-inset-md);
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 300;
  outline: none;
  padding: 12px 16px;
  resize: vertical;
  transition: background-color 0.2s;

  &:focus {
    background-color: var(--grey-100);
  }

  &::placeholder {
    color: var(--grey-600);
    opacity: 1;
  }
`;

const RecaptchaWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
`;

const SubmitButton = styled.button`
  background-color: var(--wbs-red);
  border: none;
  border-radius: 50px;
  color: var(--white);
  cursor: pointer;
  font-family: var(--raleway);
  font-size: 16px;
  font-weight: 600;
  margin-top: 10px;
  padding: 11px 24px;
  transition: all 0.2s ease;
  width: 100%;

  &:hover:not(:disabled) {
    background-color: var(--dark-wbs-red);
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background-color: var(--grey-500);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
